/**
 * AppShell — authenticated layout with header + left/top nav + content area.
 *
 * Includes:
 * - Top header: Console link, fleet name, a slot for the page's own nav
 *   (HeaderSlotContext — the Fleet Editor's facet tabs live here rather than on
 *   a row of their own), user chip + role badge.
 * - Nav: Health, Activity, Approvals, plus one entry per loaded module.
 *
 * The header row is shared, so around ~1100px it ran out of width and its
 * children painted over each other (Asana 1217649894859355). Four things hold it
 * together, and each is answering a defect rather than a taste:
 * - The row clips, and so does its left group. The group's clip is the one that
 *   matters: it is `flex-1 min-w-0`, so its children's floors can exceed the
 *   width it was given and spill over the chip while still sitting inside the
 *   header's padding box, where the header's own clip never sees them.
 * - The nav slot grows into whatever the row has left (`flex-1`, zero basis)
 *   but never below a 200px floor, so the active tab and the More button
 *   survive even when free space reaches zero. The zero basis is load-carrying
 *   twice over: it keeps the measurement independent of the current split, and it
 *   makes this wrapper the sole grower, which is what hands the strip the whole
 *   width that collapsing the chip frees — the identity `isCrowded` relies on.
 * - The account chip has two forms, and which one renders is decided by the
 *   strip, not here: `OverflowNav` is the only thing that knows the tabs'
 *   natural width, so it measures the crowding and publishes it over
 *   HeaderCrowdContext. The account is reference information read once a
 *   session, the tabs are navigated constantly, so the account yields first.
 * - The fleet name is not capped. The strip yields first: it holds the row's
 *   slack and loses it as the window narrows, and only once it is pinned at its
 *   floor does the name begin absorbing the deficit and truncating. So tabs reach
 *   the More menu earlier than they would with a capped name. That is the
 *   deliberate trade: a cap frees tab space at narrow widths but charges for it at
 *   every width, and it truncated four of the five slugs on a 2000px screen. The
 *   full name stays in `title`.
 *
 * Measured against the built CSS (headless Chrome, the default font stack, an
 * eight-facet brain and `operator@example.com`), so these are numbers rather
 * than estimates: collapsing the chip frees 205-225px, depending on the address
 * and the role badge. The chip therefore collapses below ~1330px, facets start
 * moving into **More** below ~1120px, and the row's hard floor — where the More
 * button itself would clip — is ~630px, under the 640px this was built to hold.
 *
 * Scope: only a page that fills the header slot with `OverflowNav` can publish
 * crowding, so the chip stays expanded at every width on every other page. Those
 * pages have the whole row to themselves, which is why that is enough.
 *
 * Modules are fetched once on mount; nav falls back gracefully if unavailable.
 */
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { NavLink, useLocation, useMatch, useNavigate, useResolvedPath } from 'react-router-dom'
import type { HealthInfo, MeResponse, ModuleSummary, PluginSummary } from '../api/client'
import { api } from '../api/client'
import { useCoreStatus } from '../context/CoreStatusContext'
import { HeaderCrowdProvider, type HeaderCrowdValue } from '../context/HeaderCrowdContext'
import { useHeaderSlotContent } from '../context/HeaderSlotContext'
import { usePendingChangeSignal } from '../hooks/usePendingChangeSignal'
import { useDocumentTitle } from '../hooks/useDocumentTitle'
import { ThemeToggle } from './ThemeToggle'
import { fleetDisplayName } from '../utils/fleetName'
import { pageLabel } from '../utils/pageLabel'
import { useIsNarrow, matchesNarrow } from '../hooks/useIsNarrow'

// The canonical source repo for Mission Control — the /health version is a
// commit SHA in this repo, so the footer badge links straight to it.
const MC_SOURCE_REPO_URL = 'https://github.com/roofmaxx/agentcore-maxximus'

// Duplicates the <title> in `index.html:6`. Kept as a constant rather than read
// back from `document.title`, because by the time this component unmounts the
// app has already overwritten it — any capture would snapshot the app's own
// earlier write, not the document's original title. The two are held in sync by
// pageDocumentTitle.contract.test.tsx, which reads index.html's own <title>.
const STATIC_TITLE = 'Mission Control'

interface AppShellProps {
  user: MeResponse
  fleetSlug: string
  consoleURL: string
  children: React.ReactNode
}

function roleBadgeClass(role: string): string {
  switch (role) {
    case 'fleet_owner': return 'bg-purple-100 text-purple-800'
    case 'operator':    return 'bg-blue-100 text-blue-800'
    default:            return 'bg-gray-100 text-gray-700'
  }
}

function roleBadgeLabel(role: string): string {
  switch (role) {
    case 'fleet_owner': return 'Fleet Owner'
    case 'operator':    return 'Operator'
    case 'viewer':      return 'Viewer'
    default:            return role
  }
}

/**
 * Initials for the collapsed account chip, from the email's local part.
 *
 * `anna.corbin@example.com` → `AC`; a local part with no separator gives its
 * first two letters (`operator` → `OP`), which still reads as an avatar rather
 * than a stray letter. The full address never disappears — it stays in the
 * chip's `title` and its accessible name.
 */
function emailInitials(email: string): string {
  const local = email.split('@')[0] ?? ''
  const parts = local.split(/[.\-_+]/).filter(part => part.length > 0)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[1][0]).toUpperCase()
}

/**
 * Whether the enclosing nav section is collapsed. A context rather than a prop
 * so each section's items stay plain `<NavItem>`s at the call site.
 */
const NavSectionCollapsedContext = createContext(false)

function NavItem({ to, children }: { to: string; children: React.ReactNode }) {
  const sectionCollapsed = useContext(NavSectionCollapsedContext)
  // Matched the way NavLink matches (`end` only for the root), so "is this the
  // current page" agrees with the active styling below.
  const end = to === '/'
  const isCurrent = useMatch({ path: useResolvedPath(to).pathname, end }) !== null
  const concealed = sectionCollapsed && !isCurrent
  return (
    <NavLink
      to={to}
      end={end}
      // A collapsed section still shows the page you are on, so collapsing
      // never leaves the operator without a "you are here" in the nav. The
      // `hidden` attribute, not a class: it also drops the link from the
      // accessibility tree and the tab order. The `hidden` CLASS as well,
      // because Tailwind's `block` utility outranks the UA's `[hidden]` rule.
      hidden={concealed}
      className={({ isActive }) =>
        `${concealed ? 'hidden' : 'block'} px-3 py-2 rounded-md text-sm font-medium transition-colors truncate ${
          isActive
            ? 'bg-indigo-50 text-indigo-700'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
        }`
      }
    >
      {children}
    </NavLink>
  )
}

/**
 * A collapsible nav section: a header button plus its items. Items stay in the
 * DOM while collapsed (hidden, not unmounted) so `aria-controls` always points
 * at a real element.
 *
 * `indicator` renders on the header ONLY while collapsed — a signal carried by
 * an item (the Updates badges) must not vanish because its section is folded.
 * Collapse the menu, never the state.
 */
function NavSection({
  id,
  title,
  expanded,
  onToggle,
  indicator,
  children,
}: {
  id: string
  title: string
  expanded: boolean
  onToggle: (id: string) => void
  indicator?: React.ReactNode
  children: React.ReactNode
}) {
  const bodyId = `nav-section-${id.replace(/[^a-zA-Z0-9_-]/g, '-')}`
  return (
    <div className="flex flex-col gap-1 shrink-0">
      <button
        type="button"
        onClick={() => onToggle(id)}
        aria-expanded={expanded}
        aria-controls={bodyId}
        className="flex items-center gap-1.5 w-full px-3 py-1 rounded-md text-left text-xs font-semibold text-gray-400 uppercase tracking-wider hover:bg-gray-100 hover:text-gray-600 transition-colors"
      >
        <svg
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className={`shrink-0 transition-transform duration-150 ${expanded ? 'rotate-90' : ''}`}
        >
          <polyline points="9 6 15 12 9 18" />
        </svg>
        <span className="truncate">{title}</span>
        {!expanded && indicator && (
          <span className="ml-auto inline-flex items-center gap-1 normal-case tracking-normal">
            {indicator}
          </span>
        )}
      </button>
      <div id={bodyId} className="flex flex-col gap-1">
        <NavSectionCollapsedContext.Provider value={!expanded}>
          {children}
        </NavSectionCollapsedContext.Provider>
      </div>
    </div>
  )
}

const NAV_COLLAPSED_KEY = 'mc.nav-collapsed'

/** Which core-update banner the operator has already dismissed, by commit count. */
const UPDATE_DISMISSED_KEY = 'mc.core-update-dismissed'


// The ids of the nav sections the operator has EXPANDED. Stored as the expanded
// set rather than the collapsed one so the default — all collapsed — is simply
// an absent key, and a section that appears later (a newly deployed module or
// plugin group) arrives collapsed too.
const NAV_EXPANDED_SECTIONS_KEY = 'mc.nav-expanded-sections'

function readExpandedSections(): Set<string> {
  try {
    const raw = localStorage.getItem(NAV_EXPANDED_SECTIONS_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    return new Set(Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [])
  } catch {
    return new Set()
  }
}

function writeExpandedSections(ids: Set<string>) {
  try {
    localStorage.setItem(NAV_EXPANDED_SECTIONS_KEY, JSON.stringify([...ids]))
  } catch {
    // Non-fatal: storage unavailable (private mode) — state still works in-session.
  }
}

/**
 * The collapsed chip's avatar. One constant, because the live avatar and its
 * hidden mirror must be the same box — a mirror that measures a different width
 * than the thing it stands in for makes `reserve` a lie.
 */
const CHIP_AVATAR_CLASS = 'inline-flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-700'

/**
 * The expanded chip's box, minus its `display`. Same reason as the avatar
 * constant: `reserve` is the difference between the live chip and its mirror, so
 * a class that lands on only one of the two makes that number a lie.
 *
 * `display` is the one property the two may not share, and must not be folded in
 * here: the mirror is itself the measured element and has to shrink to fit
 * (`inline-flex`), while the live chip is a flex item of the chip container
 * (`flex`).
 */
const CHIP_EXPANDED_CLASS = 'items-center gap-3'

/**
 * The expanded chip's contents — address plus role badge — written once, because
 * the live chip and the hidden mirror that prices its collapse must measure the
 * same box. A fragment, so each caller keeps its own root and its own `display`.
 */
function AccountIdentity({ user }: { user: MeResponse }) {
  return (
    <>
      <div className="text-right">
        <p className="text-sm font-medium text-gray-900">{user.email}</p>
      </div>
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${roleBadgeClass(user.fleet_role)}`}
      >
        {roleBadgeLabel(user.fleet_role)}
      </span>
    </>
  )
}

export function AppShell({ user, fleetSlug, consoleURL, children }: AppShellProps) {
  const navigate = useNavigate()
  const location = useLocation()
  // ADR-099 D8 — one shared poll (CoreStatusProvider, wired in App.tsx) backs
  // both the nav badge below and the persistent banner rendered under the header.
  const { status: coreStatus } = useCoreStatus()
  const coreUpdateAvailable = coreStatus?.available === true && coreStatus.update_available
  // ADR-122 D8b — a dot marks either operator Changes or a platform update.
  // retired ADR-114 D2 banner put this fleet-wide fact inside ONE agent's tab
  // and expressed it as a repo-wide commit count; the nav is where a fleet-wide
  // signal belongs, and a dot cannot overstate the way a count did. No number
  // here on purpose — the named list is one click away on Updates.
  const { waiting: changesWaiting, platformSyncPending } = usePendingChangeSignal(location.pathname)
  const updatesWaiting = changesWaiting || platformSyncPending
  const [modules, setModules] = useState<ModuleSummary[]>([])
  const [plugins, setPlugins] = useState<PluginSummary[]>([])
  // The running build, shown in the footer so an operator can confirm what
  // version this fleet's MC is on without SSH/curl. null until fetched (or if
  // /health is unreachable — the badge just hides).
  const [health, setHealth] = useState<HealthInfo | null>(null)
  // Sidebar collapse — persisted so the operator's choice survives reloads and
  // navigation. Hiding the nav frees real estate for the main panel; as the nav
  // grows more nested over time, collapsing it becomes more valuable.
  const isNarrow = useIsNarrow()
  // Dismissal is remembered against the COMMIT COUNT, not as a plain boolean:
  // dismissing "7 commits behind" must not hide "12 commits behind" tomorrow.
  // An operator who has seen it once should not be nagged on every page; an
  // operator who has not seen the NEW one should be.
  const [updateDismissedAt, setUpdateDismissedAt] = useState<string | null>(
    () => localStorage.getItem(UPDATE_DISMISSED_KEY),
  )
  const [navCollapsed, setNavCollapsed] = useState(
    // On a phone the nav starts CLOSED unless the operator has said otherwise.
    // Open-by-default costs 192px of a 412px screen before anything is read.
    () => {
      const stored = localStorage.getItem(NAV_COLLAPSED_KEY)
      if (stored !== null) return stored === '1'
      return matchesNarrow()
    },
  )
  const toggleNav = () => {
    setNavCollapsed(prev => {
      const next = !prev
      try {
        localStorage.setItem(NAV_COLLAPSED_KEY, next ? '1' : '0')
      } catch {
        // Non-fatal: storage unavailable (private mode) — state still works in-session.
      }
      return next
    })
  }
  // Per-section collapse — every section starts collapsed; see
  // NAV_EXPANDED_SECTIONS_KEY. The nav grows with each fleet-specific module and
  // plugin group, so folding is what keeps it navigable.
  const [expandedSections, setExpandedSections] = useState<Set<string>>(readExpandedSections)
  const toggleSection = useCallback((id: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      writeExpandedSections(next)
      return next
    })
  }, [])

  useEffect(() => {
    let cancelled = false
    api.getModules().then(res => {
      if (!cancelled) setModules(res.modules)
    }).catch(() => {
      // Non-fatal — nav just shows no modules.
    })
    api.listPlugins().then(res => {
      if (!cancelled) setPlugins(res.plugins)
    }).catch(() => {
      // Non-fatal — nav just shows no plugins.
    })
    api.getHealth().then(res => {
      if (!cancelled) setHealth(res)
    }).catch(() => {
      // Non-fatal — the version footer just doesn't render.
    })
    return () => { cancelled = true }
  }, [])

  // The rules live in utils/pageLabel so the same label can name the page on
  // more than one surface without either deriving its own. `module_id` is mapped
  // to `id` here: the resolver takes the two fields a label needs, not the DTO.
  const pageLabelText = pageLabel(
    location.pathname,
    modules.map(m => ({ id: m.module_id, title: m.title })),
    plugins,
  )

  // Plugins are grouped by nav.group, in order of first appearance; ungrouped
  // plugins fall under "Plugins". Each group is its own collapsible section.
  const pluginGroups = useMemo(() => {
    const grouped = new Map<string, PluginSummary[]>()
    for (const p of plugins) {
      const group = p.nav.group ?? ''
      if (!grouped.has(group)) grouped.set(group, [])
      grouped.get(group)!.push(p)
    }
    return Array.from(grouped.entries())
  }, [plugins])

  // Every section currently rendered — what "Expand all" expands.
  const sectionIds = useMemo(() => [
    'fleet',
    ...(modules.length > 0 ? ['modules'] : []),
    ...pluginGroups.map(([group]) => `plugins:${group}`),
  ], [modules.length, pluginGroups])
  const allExpanded = sectionIds.every(id => expandedSections.has(id))
  const toggleAllSections = () => {
    const next = allExpanded ? new Set<string>() : new Set(sectionIds)
    writeExpandedSections(next)
    setExpandedSections(next)
  }

  // The tab title is written here because the page name depends on router state.
  const fleetName = fleetDisplayName(fleetSlug)
  useDocumentTitle(fleetName ? `${pageLabelText} - ${fleetName}` : pageLabelText, fleetName || STATIC_TITLE)

  // A page's own nav, rendered inline in the header instead of on its own row.
  const headerSlot = useHeaderSlotContent()

  // ── The account chip's crowding channel ─────────────────────────────────
  // The state lives here because this component renders both the chip and the
  // provider, and cannot consume a provider it renders itself. `OverflowNav`
  // publishes `crowded` from the header slot; the chip's own mirrors below
  // publish `reserve`.
  const [crowded, setCrowded] = useState(false)
  const [chipReserve, setChipReserve] = useState<number | undefined>(undefined)
  const chipMirrorRef = useRef<HTMLDivElement>(null)

  // `publishReserve` rounds before it stores. Widths come from
  // `getBoundingClientRect` and are fractional, so without that a
  // measure → publish → re-render → re-measure cycle runs until React throws
  // "Maximum update depth exceeded". `setCrowded` needs no wrapper of its own:
  // it takes a boolean, and `useState` already bails on an unchanged value.
  //
  // The rounding is what makes the stored `reserve` differ from the width the
  // collapse really frees, by up to half a pixel. `isCrowded` absorbs that
  // residual in `CROWD_HYSTERESIS_PX` rather than this rounding being dropped:
  // an unrounded publish would put the update loop back, and rounding every
  // input instead still cannot make `round(a + r) - round(r)` equal `round(a)`.
  const publishReserve = useCallback((next: number) => {
    const rounded = Math.round(next)
    setChipReserve(prev => (prev === rounded ? prev : rounded))
  }, [])

  // `reserve` is the width the collapse frees, measured from the chip's two
  // hidden mirrors. Both of them render permanently, because this is a
  // difference of two widths and only one form is ever in the live DOM: with
  // the expanded mirror alone the compact width would be unknown while
  // expanded, and `isCrowded`'s "undefined means stay expanded" rule would
  // deadlock the chip in its expanded form forever. Measured from the pair, it
  // is a state-independent constant.
  //
  // Measured once, at mount (and again only if the identity it renders changes).
  // A web font that swaps in later therefore leaves `reserve` slightly stale —
  // the same limitation the strip's own tab widths carry, and for the same
  // reason: re-measuring on every paint is what the round-and-bail rule above
  // exists to avoid.
  useLayoutEffect(() => {
    const row = chipMirrorRef.current
    if (!row) return
    const expanded = row.querySelector<HTMLElement>('[data-chip-mirror="expanded"]')
    const compact = row.querySelector<HTMLElement>('[data-chip-mirror="compact"]')
    if (!expanded || !compact) return
    // Floored at 0: the expanded form cannot physically be narrower than the
    // compact one, and a negative reserve would invert `isCrowded`'s threshold.
    publishReserve(Math.max(
      0,
      expanded.getBoundingClientRect().width - compact.getBoundingClientRect().width,
    ))
  // `isNarrow` is a dependency because the chip's mirrors move between the two
  // headers when the breakpoint flips. AppShell itself never unmounts, so
  // without it a rotate from portrait to landscape would leave `reserve`
  // measured against a DOM node that is no longer the one in the document.
  }, [publishReserve, user.email, user.fleet_role, isNarrow])

  const crowdValue = useMemo<HeaderCrowdValue>(
    () => ({ crowded, setCrowded, reserve: chipReserve }),
    [crowded, chipReserve],
  )

  const accountLabel = `${user.email} — ${roleBadgeLabel(user.fleet_role)}`

  /* The account chip, lifted out of the desktop header so the phone header can
     render the same element. Its two hidden mirrors are what `reserve` is
     measured from, so exactly one copy must be in the document at all times —
     rendering it in whichever header is live keeps that true. */
  const accountChip = (
      <div className="relative flex items-center shrink-0">
        {/* Both forms, permanently, so `reserve` has both operands in
            either state. `absolute`/`invisible` keeps them out of flow but
            measurable; `whitespace-nowrap` is not optional — a full email
            address wraps as readily as a tab strip, and a wrapped mirror
            measures short, which would under-price the collapse.
            `aria-hidden` keeps the duplicate address out of the
            accessibility tree, and neither mirror is focusable.

            The `absolute inset-0 overflow-hidden` box around them is not
            decoration: a mirror row ~200px wider than the chip gives the
            header row and its left group — both `overflow-hidden` — that much
            permanent horizontal SCROLLABLE overflow, and an `overflow:hidden`
            box is still programmatically scrollable, so one `scrollIntoView`
            or focus-reveal inside the header could displace the whole row with
            no way back. Clipping the mirrors here keeps the overflow inside a
            box nothing scrolls, and clipping never affects
            `getBoundingClientRect`, so the measurement is untouched. */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div
            ref={chipMirrorRef}
            aria-hidden="true"
            className="invisible whitespace-nowrap"
          >
            <div data-chip-mirror="expanded" className={`inline-flex ${CHIP_EXPANDED_CLASS}`}>
              <AccountIdentity user={user} />
            </div>
            <div data-chip-mirror="compact" className="inline-flex items-center">
              <span className={CHIP_AVATAR_CLASS}>{emailInitials(user.email)}</span>
            </div>
          </div>
        </div>

        {/* One element per form, both tagged `account-chip`, because the
            mirrors above repeat the email and the role badge verbatim: a
            test looking for either by text would otherwise find two. */}
        {(crowded || isNarrow) ? (
          /* The compact form. The address and role do not disappear — they
             move into `title` for the pointer and into the accessible name
             for assistive technology, so the avatar still answers "who am I
             signed in as".

             `role="img"` is what makes `aria-label` the accessible name: a
             bare `span` has no role, and a label on a role-less element is
             ignored, so the initials would be all a screen reader got. */
          <span
            data-testid="account-chip"
            role="img"
            aria-label={accountLabel}
            title={accountLabel}
            className={CHIP_AVATAR_CLASS}
          >
            {emailInitials(user.email)}
          </span>
        ) : (
          <div data-testid="account-chip" className={`flex ${CHIP_EXPANDED_CLASS}`}>
            <AccountIdentity user={user} />
          </div>
        )}
      </div>
  )

  return (
    <HeaderCrowdProvider value={crowdValue}>
      {/* `h-screen` is 100vh, which on a phone is TALLER than the visible area while the
          browser's URL bar is showing — so the bottom of the shell, which is where Chat docks
          its composer, sat below the fold with no way to scroll to it. `dvh` is the dynamic
          viewport unit that tracks the bar; it is applied through `supports-[]` so a browser
          without it keeps the `h-screen` it has always had rather than ending up with no
          height at all. */}
        <div className="h-screen supports-[height:100dvh]:h-dvh bg-gray-50 flex flex-col overflow-hidden">
        {isNarrow ? (
          <>
          {/* 🔴 A PHONE GETS ITS OWN HEADER, NOT A NARROWED DESKTOP ONE.
              The desktop row packs a burger, a Console link, the fleet name, a
              breadcrumb and a page's facet tabs into one `overflow-hidden` group
              whose children's floors already add up past 500px. On a 412px screen
              that group is sized to `header − chip`, its contents overflow their
              content box, and the clip takes whatever is past the edge — which is
              how an operator ended up on 2026-10-10 unable to reach the one
              control that closes the nav.

              Collapsing the chip buys that row ~150px. It does not buy it enough,
              and every pixel of it is spent re-deriving a layout the phone does
              not want anyway. So below `md` the shell renders THIS instead: three
              children, each with a floor, adding to ~92px, and no `overflow-hidden`
              anywhere on the row for a clip to happen in. The burger cannot be
              pushed out because nothing in the row is able to grow past it.

              Nothing is lost — the Console link moves into the drawer and the
              page label takes the second line of the title. The desktop header below is
              untouched, which is deliberate: its flex and measurement rules are
              load-bearing and this change must not re-derive them. */}
          <header className="bg-white border-b border-gray-200 px-2 py-2 flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={toggleNav}
              aria-label={navCollapsed ? 'Show navigation' : 'Hide navigation'}
              aria-expanded={!navCollapsed}
              data-testid="nav-toggle-mobile"
              /* `p-3` on an 18px glyph is a 42px target, against the 44px both
                 platform guidelines ask for and the ~30px the desktop button is.
                 A control an operator needs on every page should not need aim. */
              className="shrink-0 rounded-md p-3 text-gray-600 hover:bg-gray-100 active:bg-gray-200 transition-colors"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>

            {/* The only element allowed to absorb the row's deficit: `min-w-0`
                lets each line shrink below its text and `truncate` ends it in
                an ellipsis. The full text stays in `title`.

                Two lines, not one. The first draft showed the page label alone
                and dropped the fleet name whenever a page had one — which is
                every page. An operator running six fleets needs "which fleet am
                I about to change" answered on the page itself, and the drawer
                already answers "which page". So the fleet name keeps the line
                it has on desktop and the page label takes a muted second one,
                which is also what makes this read as a title rather than a
                squeezed desktop row. */}
            <span className="flex-1 min-w-0 leading-tight">
              <span
                className="block truncate text-sm font-semibold text-gray-900 capitalize"
                title={`${fleetSlug} Mission Control`}
              >
                {fleetSlug} Mission Control
              </span>
              {pageLabelText && (
                <span
                  className="block truncate text-xs text-gray-500"
                  data-testid="page-breadcrumb-mobile"
                  title={pageLabelText}
                >
                  {pageLabelText}
                </span>
              )}
            </span>

            {accountChip}
          </header>

          {/* A page's facet tabs get the full width of their own row here rather
              than competing for the header's. Scrolls sideways instead of being
              clipped, so a tab is always reachable however many there are. */}
          {headerSlot && (
            <div className="shrink-0 border-b border-gray-200 bg-white px-2 py-1.5 overflow-x-auto">
              {headerSlot}
            </div>
          )}
          </>
        ) : (
          /* Top header. `overflow-hidden` on BOTH this row and the left group: the
              header's clip alone does not stop the overlap, because the left group
              is `flex-1 min-w-0` and so is sized to `header inner − chip` whatever
              its content is. When its children's floors exceed that width they
              spill out of the group's content box and paint over the `shrink-0`
              chip while still sitting inside the header's padding box, where the
              header's own clip never sees them. That is the mechanism of the
              overlap bug. The `fixed` overflow-menu panel escapes both clips: no
              ancestor here sets `transform`, `filter` or `contain`, so nothing
              establishes a containing block for it. */
          <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between shrink-0 overflow-hidden">
            {/* The insets are the clip's breathing room, not spacing: no header
                control declares a `focus:ring`, so they all rely on the UA outline,
                which paints outside the border box. The group's height is set by the
                30px burger and leaves ~1px of slack around the 28px facet tabs, so
                an edge-tight clip would eat the burger's outline and every tab's top
                and bottom.

                `-my-1 py-1` and `-mr-1.5 pr-1.5` each pair a padding with an equal
                negative margin, so the content box is unchanged on those three
                sides. The right pair is not optional: the slot wrapper is the last
                child and grows to the group's right edge, so without it the
                right-most tab's (or the More button's) outline is the one clipped.

                `-ml-3 pl-1.5` is deliberately UNEQUAL — 12px out, 6px back — and
                that 6px difference is the burger's optical alignment. It used to be
                a `-ml-1.5` on the button, an overhang the group's clip would have
                cut, and moving it onto the group needed the extra 6px of negative
                margin: an equal `-ml-1.5 pl-1.5` pair leaves the content box where
                it was, so the burger lands 6px right of the header's 24px `px-6`
                gutter and every control after it follows. With `-ml-3 pl-1.5` the
                content box starts at the gutter minus 6px, the burger's border box
                sits at 18px and its glyph at 24px — flush with the gutter, as on
                every other page — and the 6px still left over between the group's
                padding box and the burger is the outline's room. */}
            <div className="flex items-center gap-4 min-w-0 flex-1 overflow-hidden -my-1 py-1 -ml-3 pl-1.5 -mr-1.5 pr-1.5">
              <button
                type="button"
                onClick={toggleNav}
                aria-label={navCollapsed ? 'Show navigation' : 'Hide navigation'}
                aria-expanded={!navCollapsed}
                title={navCollapsed ? 'Show navigation' : 'Hide navigation'}
                className="shrink-0 rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              </button>

              <a
                href={consoleURL}
                className="shrink-0 text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 transition-colors"
                title="Back to AgentFleet Console"
              >
                <span aria-hidden="true">←</span>
                <span>Console</span>
              </a>

              {/* Truncates rather than wraps. The header shares its row with a
                  page's own nav (HeaderSlotContext), and a fleet name long enough to
                  push the tabs onto a second line costs more than the characters it
                  would have shown.

                  No width cap, deliberately. This carried `max-w-48` while the
                  header slot was filled, which meant every Fleet Editor page
                  truncated the name at 192px at EVERY width — four of the five slugs
                  measure past it (matthaios 239, spartan 221, athena 213, orion 198),
                  so a 2000px window still showed "Matthaios Mission …" with hundreds
                  of pixels going spare.

                  What the cap bought was earlier relief for the tab strip, and the
                  mechanism has two regimes worth separating, because conflating them
                  gets the yielding element backwards. While free space is POSITIVE
                  the wrapper below is the sole grower and holds all the slack, so
                  narrowing the window takes that slack from the strip and this name
                  is untouched — the wrapper is not absorbing a deficit, it is losing
                  growth. Once free space goes NEGATIVE the roles inverse: shrinkage
                  is distributed by `shrink factor × base size`, the wrapper's zero
                  basis gives it a zero share and its floor pins it at 200px, and THIS
                  name absorbs the whole deficit down to its own 96px floor.

                  So the strip yields first, until it bottoms out. Removing the cap
                  gives that back — tabs reach the More menu at a wider window than
                  they used to. That is the accepted cost, and it is cheap: the menu
                  holds them, the strip's floor guarantees the active tab and the
                  button, and nothing overlaps.

                  The obvious alternative — give the wrapper a real basis so the
                  deficit splits between it and this name — was built, measured and
                  abandoned. It breaks `isCrowded`: the chip's freed width then splits
                  too, so the strip receives ~72% of a `reserve` the predicate credits
                  in full, and the row locks into a collapse/expand render loop around
                  1100px. See the wrapper's comment.

                  `min-w-24` floors it so it can never render as a bare ellipsis;
                  `shrink` is what lets it give up width at all, and the 96px floor is
                  what stops there. The full name always stays in `title`. */}
              <span
                className="text-xl font-semibold text-gray-900 capitalize truncate shrink min-w-24"
                title={`${fleetSlug} Mission Control`}
              >
                {fleetSlug} Mission Control
              </span>

              {/* Permanently true today — `pageLabel` gives every path a label. Kept
                  so the breadcrumb cannot render a bare `/` separator if that ever
                  stops holding. */}
              {pageLabelText && (
                <>
                  <span className="text-gray-300 shrink-0" aria-hidden="true">/</span>
                  {/* The testid is a test hook: the nav below renders links whose
                      text is identical to these labels, so an unscoped query for
                      "Investigations" matches two nodes. */}
                  <span
                    data-testid="page-breadcrumb"
                    className="text-sm font-medium text-gray-600 shrink-0"
                  >
                    {pageLabelText}
                  </span>
                </>
              )}

              {/* A page's own navigation, hosted here rather than on a row of its
                  own. The Fleet Editor's facet tabs are the only filler today; the
                  breadcrumb left this row half empty and the tabs were costing a
                  full ~50px band under it. Empty for every other page. */}
              {headerSlot && (
                <>
                  <span className="text-gray-200 shrink-0" aria-hidden="true">|</span>
                  {/* `flex-1 min-w-[200px]` — a zero basis (`flex: 1 1 0%`), so the
                      wrapper grows into whatever the row has left over rather than
                      sizing to its content. Two separate reasons it must stay zero,
                      and the second one cost a rewrite to learn:

                      1. A content-derived basis (`auto`) would make a measurement of
                         this element a function of the current split — hide one tab,
                         report a narrower box, hide another, never recover.

                      2. A zero basis makes this wrapper the sole grower, which is
                         what makes collapsing the account chip hand the strip the
                         WHOLE freed width. `isCrowded` subtracts the full `reserve`
                         in its collapsed branch, so that identity is what keeps the
                         predicate's two states consistent. Give this wrapper a real
                         basis and the freed width splits with the fleet name by base
                         size — the strip gets ~72% of it while the predicate still
                         credits 100%, a ~60px over-credit that the 1px hysteresis
                         band cannot absorb. Measured: the row locks into an infinite
                         collapse/expand render loop around 1100px.

                      The floor is required, not decorative. Flex distributes
                      shrinkage by `shrink factor × base size`, and a base of zero
                      gives this wrapper a zero share — so the moment free space
                      reaches zero it would be zero wide, and the clips above would
                      swallow the active tab and the More button. 200px is derived
                      from the widest active label plus a 2px gap plus the
                      upper-bound `More (8)` label — measured against the built CSS
                      as 97.3 + 2 + 77.3 = 176.6px, rounded up with room to spare. It
                      is a constant, so it does not reintroduce content-dependent
                      measurement. */}
                  <div className="flex-1 min-w-[200px]">{headerSlot}</div>
                </>
              )}
            </div>

            {/* User chip — two forms, and the mirrors that price the swap.
                `relative` is the mirrors' containing block; they are out of flow,
                so the chip's own width is still whichever form is rendered.

                Which form shows is decided by `OverflowNav`, not here: the strip
                is the only thing that knows the tabs' natural width and the space
                they were given. The account is reference information an operator
                reads once a session, so it yields before the facet tabs they
                navigate with constantly. */}
            {accountChip}
          </header>
        )}

        {/* ADR-099 D8 — persistent "core update available" banner, shown on
            EVERY page while update_available is true, so it's seen regardless
            of tab. Silent (no banner) when up to date OR when the status is
            unavailable — a fetch failure should never nag the operator with a
            claim it can't back up. */}
        {coreUpdateAvailable && updateDismissedAt !== String(coreStatus.commits_behind) && (
          /* 🔴 DISMISSIBLE, AND IT WAS NOT. On a phone this banner wrapped to
             four lines and took a third of the screen on EVERY page, with no
             way to close it (screenshot, 2026-10-10). It is a standing notice,
             not an alert — an operator who has read it should be able to put it
             away and still find it under Fleet, where the same count is badged. */
          <div className="shrink-0 border-b border-indigo-200 bg-indigo-50 px-3 py-2 md:px-6 md:py-2.5 flex items-center justify-between gap-2 md:gap-4">
            <p className="text-xs md:text-sm text-indigo-800 min-w-0">
              <span aria-hidden="true">⬆</span>{' '}
              {/* The sentence shortens on a phone rather than wrapping to four lines. */}
              <span className="hidden sm:inline">Core update available — this fleet is{' '}</span>
              <span className="sm:hidden">Update available —{' '}</span>
              <span className="font-semibold">{coreStatus.commits_behind}</span>{' '}
              commit{coreStatus.commits_behind === 1 ? '' : 's'} behind.
            </p>
            <div className="shrink-0 flex items-center gap-1">
              <button
                type="button"
                onClick={() => navigate('/updates')}
                className="px-3 py-1 text-xs font-medium bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
              >
                Review →
              </button>
              <button
                type="button"
                aria-label="Dismiss this update notice"
                title="Dismiss — it returns when there is a newer update"
                onClick={() => {
                  const seen = String(coreStatus.commits_behind)
                  localStorage.setItem(UPDATE_DISMISSED_KEY, seen)
                  setUpdateDismissedAt(seen)
                }}
                className="px-2 py-1 text-indigo-700 rounded hover:bg-indigo-100 transition-colors"
              >
                <span aria-hidden="true">✕</span>
              </button>
            </div>
          </div>
        )}

        {/* Body: sidebar nav + content */}
        <div className="flex flex-1 min-h-0">
          {/* Sidebar nav — collapses to zero width; the header toggle reopens it.
              Scrolls vertically, so every entry stays reachable however short
              the window; each section folds under its header (all folded by
              default). */}
          {/* 🔴 ON A PHONE THE NAV FLOATS; ON A DESKTOP IT IS A COLUMN.
              As a column it takes 192px of layout width, which on a 412px screen
              left a plugin roughly half a screen wide and unreadable — the
              reason this change exists. Overlaid, opening the nav costs the
              content nothing, and a tap anywhere else closes it. */}
          {isNarrow && !navCollapsed && (
            <button
              type="button"
              aria-label="Close navigation"
              onClick={toggleNav}
              className="fixed inset-0 z-30 bg-gray-900/40"
            />
          )}
          <nav
            aria-hidden={navCollapsed}
            onClick={() => { if (isNarrow) setNavCollapsed(true) }}
            className={`bg-white flex flex-col gap-3 overflow-y-auto overflow-x-hidden whitespace-nowrap transition-[width,padding] duration-200 ease-in-out ${
              isNarrow ? 'fixed top-0 bottom-0 left-0 z-40 shadow-xl' : 'shrink-0'
            } ${
              navCollapsed
                ? 'w-0 p-0 border-r-0'
                : `${isNarrow ? 'w-64' : 'w-48'} p-3 border-r border-gray-200`
            }`}
          >
            <button
              type="button"
              onClick={toggleAllSections}
              className="shrink-0 self-end px-2 py-0.5 rounded text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors"
            >
              {allExpanded ? 'Collapse all' : 'Expand all'}
            </button>

            <NavSection
              id="fleet"
              title="Fleet"
              expanded={expandedSections.has('fleet')}
              onToggle={toggleSection}
              indicator={(updatesWaiting || coreUpdateAvailable) && (
                <>
                  {updatesWaiting && (
                    <span
                      aria-label={!expandedSections.has('fleet') ? 'Updates are waiting to go live' : undefined}
                      title="Updates are waiting to go live in this fleet"
                      className="inline-block w-2 h-2 rounded-full bg-amber-500"
                    />
                  )}
                  {coreUpdateAvailable && (
                    <span
                      className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-indigo-600 text-white text-[11px] font-semibold"
                      title={`Updates: ${coreStatus.commits_behind} commit${coreStatus.commits_behind === 1 ? '' : 's'} behind core`}
                    >
                      {coreStatus.commits_behind}
                    </span>
                  )}
                </>
              )}
            >
              <NavItem to="/">Health</NavItem>
              <NavItem to="/activity">Activity</NavItem>
              <NavItem to="/approvals">Approvals</NavItem>
              <NavItem to="/improvements">Improvements</NavItem>
              <NavItem to="/investigations">Investigations</NavItem>
              <NavItem to="/feedback">Feedback</NavItem>
              {(user.fleet_role === 'operator' || user.fleet_role === 'fleet_owner') && (
                <NavItem to="/logs">Logs</NavItem>
              )}
              {(user.fleet_role === 'operator' || user.fleet_role === 'fleet_owner') && (
                <NavItem to="/diagnostics">Diagnostics</NavItem>
              )}
              {(user.fleet_role === 'operator' || user.fleet_role === 'fleet_owner') && (
                <NavItem to="/llm-usage">LLM Usage</NavItem>
              )}
              <NavItem to="/in-flight">In flight</NavItem>
              <NavItem to="/chat">Chat</NavItem>
              <NavItem to="/fleet-editor">Fleet Editor</NavItem>
              {/* ADR-099 D8 — top-level Updates nav item with a count badge when a
                  core update is available (the count is commits_behind — how far
                  behind core main this fleet is).
                  ADR-122 D8b — one dot covers operator Changes or platform syncs;
                  the core update badge remains separate. */}
              <NavItem to="/updates">
                <span className="inline-flex items-center gap-1.5">
                  Updates
                  {updatesWaiting && (
                    <span
                      aria-label={expandedSections.has('fleet') ? 'Updates are waiting to go live' : undefined}
                      title="Updates are waiting to go live in this fleet"
                      className="inline-block w-2 h-2 rounded-full bg-amber-500"
                    />
                  )}
                  {coreUpdateAvailable && (
                    <span
                      className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-indigo-600 text-white text-[11px] font-semibold"
                      title={`${coreStatus.commits_behind} commit${coreStatus.commits_behind === 1 ? '' : 's'} behind core`}
                    >
                      {coreStatus.commits_behind}
                    </span>
                  )}
                </span>
              </NavItem>
            </NavSection>

            {modules.length > 0 && (
              <NavSection
                id="modules"
                title="Modules"
                expanded={expandedSections.has('modules')}
                onToggle={toggleSection}
              >
                {modules.map(m => (
                  <NavItem key={m.module_id} to={`/modules/${m.module_id}`}>
                    {m.icon ? `${m.icon} ` : ''}{m.title}
                  </NavItem>
                ))}
              </NavSection>
            )}

            {/* Plugin nav entries — server RBAC-filtered by fleet_role; rendered in
                declared order. nav.position is metadata for future ordering logic (P1). */}
            {pluginGroups.map(([group, groupPlugins]) => (
              <NavSection
                key={group || '__plugins__'}
                id={`plugins:${group}`}
                title={group || 'Plugins'}
                expanded={expandedSections.has(`plugins:${group}`)}
                onToggle={toggleSection}
              >
                {groupPlugins.map(p => (
                  <NavItem key={p.id} to={`/plugins/${p.id}`}>
                    {p.icon ? `${p.icon} ` : ''}{p.title}
                  </NavItem>
                ))}
              </NavSection>
            ))}

            {/* Help — the in-app user guide, pinned at the bottom of the nav. */}
            <div className="mt-auto pt-3 shrink-0">
              {/* The phone header has no room for the Console link and does not
                  try to find any; it lives here instead, so the way back out of
                  a fleet is still one tap from every page. Desktop keeps it in
                  the header, where there is room and where it has always been —
                  rendering it twice would put two "Console" links in the a11y
                  tree at the same time. */}
              {isNarrow && (
                <a
                  href={consoleURL}
                  className="block px-3 py-2 rounded-md text-sm text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                >
                  <span aria-hidden="true">←</span> Console
                </a>
              )}
              <NavItem to="/help">📘 User Guide</NavItem>
              <div className="mt-2">
                <ThemeToggle />
              </div>
            </div>
          </nav>

          {/* Main content */}
          <main className="flex-1 px-3 py-4 md:px-6 md:py-6 overflow-auto min-w-0">{children}</main>
        </div>

        {/* Version footer — at-a-glance "what build is this fleet running", so an
            operator can confirm a deploy landed without SSH/curl to /health. */}
        {health && <VersionFooter health={health} />}
      </div>
    </HeaderCrowdProvider>
  )
}

// VersionFooter renders the slim build badge pinned to the bottom of the shell:
// "<fleet> · v<short-sha> ↗   ● healthy". The version links to the exact
// agentcore commit the image was built from (unless it's an unstamped "dev"
// build, which isn't a real commit and so isn't linked).
function VersionFooter({ health }: { health: HealthInfo }) {
  const isStamped = health.version !== '' && health.version !== 'dev'
  const shortSha = isStamped ? health.version.slice(0, 7) : health.version || 'unknown'
  const ok = health.status === 'ok'
  return (
    <footer className="shrink-0 border-t border-gray-200 bg-white px-6 py-1.5 flex items-center justify-between text-xs text-gray-500">
      <div className="flex items-center gap-1.5">
        <span className="capitalize font-medium text-gray-600">{health.fleet_slug || 'fleet'}</span>
        <span className="text-gray-300" aria-hidden="true">·</span>
        {isStamped ? (
          <a
            href={`${MC_SOURCE_REPO_URL}/commit/${health.version}`}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-gray-600 hover:text-indigo-700 hover:underline inline-flex items-center gap-0.5"
            title={`Mission Control build ${health.version} — view commit`}
          >
            v{shortSha}
            <span aria-hidden="true">↗</span>
          </a>
        ) : (
          <span className="font-mono text-gray-500" title="Unstamped local/dev build">v{shortSha}</span>
        )}
      </div>
      <span className="inline-flex items-center gap-1.5" title={`/health status: ${health.status}`}>
        <span
          className={`h-1.5 w-1.5 rounded-full ${ok ? 'bg-green-500' : 'bg-amber-500'}`}
          aria-hidden="true"
        />
        {ok ? 'healthy' : health.status}
      </span>
    </footer>
  )
}
